import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
} from "@mui/material";
import {
  ArrowBack,
  RecordVoiceOver,
  Group as GroupIcon,
  History as HistoryIcon,
  HelpOutline as HelpOutlineIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { usePageHeader } from "../../context/LayoutContext";
import { useToast } from "../../shared/toast";
import { ConfirmDialog } from "../../shared/ui/ConfirmDialog";
import { VideoData } from "../../shared/interfaces/VideoData";
import { getEvidenceData } from "@/features/reports/api/reportApi";
import { useOrgId } from "../../shared/auth/orgContext";
import { usePermissions } from "@/shared/hooks/usePermissions";
import { canManageEvidenceShares } from "../../shared/auth/authorization";
import { ShareEvidenceModal } from "@/features/evidence/components/ShareEvidenceModal";
import { canDownloadTranscript } from "@/shared/auth/authorization";
import { useUserDirectory } from "@/features/users/api/userDirectory";
import { getTranscriptVersion } from "../../shared/api/transcriptsApi";
import { useTranscript } from "./hooks/useTranscript";
import { useTranscriptVersions } from "./hooks/useTranscriptVersions";
import { useUpdateTranscript } from "./hooks/useUpdateTranscript";
import { useVideoController } from "./hooks/useVideoController";
import {
  useTranscriptActions,
  useTranscriptDraft,
} from "./hooks/useTranscriptDraft";
import {
  stripUids,
  useCanRedo,
  useCanUndo,
  useTranscriptDraftStore,
  useTranscriptDraftTemporal,
} from "./store/transcriptDraftStore";
import { useRangeArming } from "./hooks/useRangeArming";
import { EditorVideoPlayer } from "./components/EditorVideoPlayer";
import { TranscriptEntryList } from "./components/TranscriptEntryList";
import { SpeakerManagerDialog } from "./components/SpeakerManagerDialog";
import { SpeakerQuickAssignDialog } from "./components/SpeakerQuickAssignDialog";
import { SaveDiffDialog } from "./components/SaveDiffDialog";
import { TranscriptHistoryDialog } from "./components/TranscriptHistoryDialog";
import {
  TranscriptTutorialDialog,
  TutorialReveal,
} from "./components/TranscriptTutorialDialog";
import { PendingEditsBar } from "./components/PendingEditsBar";
import { diffTranscripts } from "./utils/transcriptDiff";
import { useTranscriptDownload } from "./hooks/useTranscriptDownload";
import { DownloadTranscriptLanguageModal } from "../../shared/ui/modals/DownloadTranscriptLanguageModal";
import {
  TimeRangeSelector,
  RangeMarker,
} from "./components/TimeRangeSelector";
import { AddEntryComposer } from "./components/AddEntryComposer";
import { formatSpeakerLabel } from "./components/speakerColors";
import { highestConfidenceSamplesBySpeaker } from "./utils/speakerSamples";

/** localStorage flag: the first-run editor walkthrough has been shown. */
const TUTORIAL_SEEN_KEY = "transcriptEditorTutorialSeen:v1";

/** localStorage key prefix: the speaker quick-assign prompt has been shown for a transcript. */
const QUICK_ASSIGN_SEEN_KEY = "transcriptSpeakerQuickAssignSeen:";

const TranscriptViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { evidenceId } = useParams<{ evidenceId: string }>();
  const toast = useToast();
  const canDownload = canDownloadTranscript(usePermissions());

  const { loaded, isLoading, error, refetch } = useTranscript(evidenceId);
  const { mutate, isLoading: isSaving } = useUpdateTranscript();
  const {
    versions,
    isLoading: versionsLoading,
    refetch: refetchVersions,
  } = useTranscriptVersions(evidenceId);

  const controller = useVideoController();
  const { draft, paragraphs, speakers, isDirty, canSave, validation } =
    useTranscriptDraft(controller.duration);
  const {
    initStore,
    destroyStore,
    discardChanges,
    setEntryRange,
    addEntry,
  } = useTranscriptActions();
  const original = useTranscriptDraftStore((s) => s.original);

  const { undo, redo } = useTranscriptDraftTemporal();
  const canUndo = useCanUndo();
  const canRedo = useCanRedo();

  const organizationId = useOrgId();
  const { resolveName } = useUserDirectory(organizationId ?? undefined);
  const permissions = usePermissions();
  const { user } = useAuth();

  const arming = useRangeArming({ controller, setEntryRange, addEntry });

  const [editingDisabled, setEditingDisabled] = useState(false);
  const [speakerMgrOpen, setSpeakerMgrOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // Version currently shown in the editor (null until the first load resolves).
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  // A version the user picked while dirty — pending a discard confirmation.
  const [pendingVersion, setPendingVersion] = useState<number | null>(null);
  // Set when the user hits "back" while dirty — pending a discard confirmation.
  // Tracks which affordance asked, because confirming a browser-back must pop
  // real history (past the dirty-guard sentinel) while the in-page back
  // buttons navigate to the list route.
  const [pendingBack, setPendingBack] = useState<false | "button" | "browser">(
    false,
  );
  const [loadingVersion, setLoadingVersion] = useState(false);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [tutorialReveal, setTutorialReveal] = useState<TutorialReveal | undefined>();
  const tutorialChecked = useRef(false);
  // True while the first-run walkthrough is showing, so the speaker quick-assign
  // prompt waits its turn instead of stacking on top of it.
  const tutorialActive = useRef(false);
  const [quickAssignOpen, setQuickAssignOpen] = useState(false);
  // evidenceId the quick-assign auto-open has already been evaluated for.
  const quickAssignCheckedFor = useRef<string | null>(null);
  const [video, setVideo] = useState<VideoData | null>(null);
  // Tracks a failed evidence-details load: uploader identity comes only from
  // that best-effort fetch, so a failure must not silently deny the uploader
  // their share controls.
  const [videoLoadFailed, setVideoLoadFailed] = useState(false);

  // Server-side download flow (format menu → language modal). Downloads reflect
  // unsaved changes by saving the draft first (see saveForDownloadRef below);
  // an unedited transcript downloads the version currently being viewed.
  const saveForDownloadRef = useRef<() => Promise<number | null>>(
    async () => null,
  );
  const download = useTranscriptDownload(evidenceId ?? null, {
    isDirty,
    currentVersion: viewingVersion,
    save: () => saveForDownloadRef.current(),
  });

  // Leaving the editor with unsaved edits should prompt first, mirroring the
  // discard-confirmation used when switching versions. Both back entry points
  // (the app-bar back arrow and the "Back to transcripts" button) go through here.
  const requestBack = () => {
    if (isDirty) {
      setPendingBack("button");
    } else {
      navigate("/transcripts");
    }
  };
  // usePageHeader only re-registers its header when title/subtitle/chip change,
  // so its `onBack` closure would otherwise capture a stale `isDirty`. Route it
  // through a ref that always holds the latest handler.
  const requestBackRef = useRef(requestBack);
  requestBackRef.current = requestBack;

  usePageHeader({
    title: "Transcript",
    subtitle: evidenceId ? `Evidence ${evidenceId}` : undefined,
    icon: <RecordVoiceOver />,
    onBack: () => requestBackRef.current(),
  });

  // Initialize the working-copy store whenever a fresh document loads.
  useEffect(() => {
    if (loaded && evidenceId) {
      initStore(loaded, evidenceId);
      setViewingVersion(loaded.versionNumber);
    }
  }, [loaded, evidenceId, initStore]);

  // Tear down the draft when leaving the page.
  useEffect(() => () => destroyStore(), [destroyStore]);

  // Unsaved-edits guards for browser-initiated exits. The in-page back
  // affordances funnel through requestBack, but the browser offers its own
  // escape hatches. While the draft is dirty (same `isDirty` predicate the
  // in-page guard uses):
  //  • refresh / tab-close → native beforeunload prompt;
  //  • browser back → sentinel-history trap that re-opens the discard dialog.
  // Residual gap: in-app navigation to OTHER routes (sidebar links, etc.)
  // cannot be intercepted here — useBlocker/unstable_usePrompt need a data
  // router, which <BrowserRouter> is not. Closing that gap requires the
  // createBrowserRouter migration.
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Chrome still requires returnValue for the prompt to appear.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Sentinel-history trap: push a duplicate of the current entry so the
    // first browser-back lands back on this same URL, where we re-arm the
    // sentinel and ask for confirmation instead of silently discarding.
    // Spreading the existing state preserves react-router's own entry fields.
    const guardedPath = window.location.pathname;
    window.history.pushState(
      { ...window.history.state, transcriptEditGuard: true },
      "",
    );

    const handlePopState = (e: PopStateEvent) => {
      // The pop left this route entirely — e.g. the discard-confirmed
      // navigate(-2) jumping past the sentinel. Nothing left to guard.
      if (window.location.pathname !== guardedPath) return;
      // Landed on another sentinel (a dirty→clean neutralizing back() racing
      // a new dirty session): that entry keeps guarding on its own.
      if (
        (e.state as { transcriptEditGuard?: boolean } | null)
          ?.transcriptEditGuard
      ) {
        return;
      }
      // Re-arm the trap, then surface the same discard confirmation the
      // in-page back button uses. Close the version-swap variant if it's up
      // so two "Discard unsaved changes?" dialogs never stack.
      window.history.pushState(
        { ...window.history.state, transcriptEditGuard: true },
        "",
      );
      setPendingVersion(null);
      setPendingBack("browser");
    };
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      // Draft became clean (saved/discarded) or the page is unmounting: if
      // we're still parked on the sentinel, consume it so a single back now
      // leaves normally instead of eating one press on a same-URL no-op.
      if (
        (window.history.state as { transcriptEditGuard?: boolean } | null)
          ?.transcriptEditGuard
      ) {
        window.history.back();
      }
    };
  }, [isDirty]);

  // First visit ever → auto-show the walkthrough (once), then remember it.
  useEffect(() => {
    if (!draft || tutorialChecked.current) return;
    tutorialChecked.current = true;
    try {
      if (!localStorage.getItem(TUTORIAL_SEEN_KEY)) {
        tutorialActive.current = true;
        setTutorialOpen(true);
        localStorage.setItem(TUTORIAL_SEEN_KEY, "1");
      }
    } catch {
      /* localStorage unavailable → just skip the auto-tour */
    }
  }, [draft]);

  // First open of a given transcript → prompt the reviewer to name its speakers.
  // Deferred while the first-run walkthrough is showing (tutorialActive), then
  // re-evaluated once it closes (tutorialOpen flips false).
  useEffect(() => {
    if (!draft || !evidenceId) return;
    if (tutorialActive.current) return;
    if (quickAssignCheckedFor.current === evidenceId) return;
    quickAssignCheckedFor.current = evidenceId;
    try {
      const key = QUICK_ASSIGN_SEEN_KEY + evidenceId;
      if (
        !localStorage.getItem(key) &&
        highestConfidenceSamplesBySpeaker(draft).length > 0
      ) {
        setQuickAssignOpen(true);
        localStorage.setItem(key, "1");
      }
    } catch {
      /* localStorage unavailable → just skip the auto-prompt */
    }
  }, [draft, evidenceId, tutorialOpen]);

  // Load the video for side-by-side playback.
  useEffect(() => {
    if (!evidenceId) return;
    let cancelled = false;
    setVideoLoadFailed(false);
    getEvidenceData(evidenceId)
      .then((v) => {
        if (!cancelled) setVideo(v);
      })
      .catch(() => {
        /* video is best-effort; transcript editing still works without it */
        if (!cancelled) setVideoLoadFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [evidenceId]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error, toast]);

  // Let the walkthrough drive the relevant dialogs so the step shows the real UI.
  // Runs only when the tour changes `tutorialReveal`, so it won't fight manual
  // opens; leaving the step (or closing the tour) closes the tour-opened dialog.
  useEffect(() => {
    setSpeakerMgrOpen(tutorialReveal === "speakers-dialog");
    setHistoryOpen(tutorialReveal === "history-dialog");
  }, [tutorialReveal]);

  const previewTimeControls = tutorialReveal === "time-controls";

  const diff = useMemo(
    () => diffTranscripts(original, draft),
    [original, draft],
  );

  // Highest saved version — falls back to the initially loaded version until the
  // history list resolves.
  const latestVersion = useMemo(() => {
    if (versions.length > 0) {
      return Math.max(...versions.map((v) => v.versionNumber));
    }
    return loaded?.versionNumber ?? null;
  }, [versions, loaded]);

  const viewingOldVersion =
    viewingVersion != null &&
    latestVersion != null &&
    viewingVersion >= 0 &&
    viewingVersion !== latestVersion;

  const invalidCount = useMemo(
    () =>
      Object.values(validation.entries).filter(
        (e) => e.needsRangeReselect || e.invalidRange || e.emptyText,
      ).length,
    [validation],
  );

  const markers: RangeMarker[] = useMemo(
    () =>
      paragraphs.map((p) => ({
        uid: p._uid as string,
        start: p.start,
        end: p.end,
        speaker: p.speaker,
        active:
          p.start <= controller.currentTime && controller.currentTime < p.end,
      })),
    [paragraphs, controller.currentTime],
  );

  const armedLabel = useMemo(() => {
    if (!arming.armed) return undefined;
    if (arming.armed.target.kind === "new") return "New entry";
    const entry = paragraphs.find(
      (p) => p._uid === arming.armedUid,
    );
    return entry
      ? `Editing ${formatSpeakerLabel(entry.speaker)}`
      : "Editing entry";
  }, [arming.armed, arming.armedUid, paragraphs]);

  if (!evidenceId) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">No evidence ID provided.</Alert>
      </Container>
    );
  }

  const disabled = editingDisabled || isSaving;

// Same gate the share endpoints enforce: uploader or station-wide reader.
  // Uploader identity comes only from the best-effort evidence-details fetch;
  // if that fetch failed we can't tell, so treat the viewer as a possible
  // uploader rather than lock the real uploader out — the share endpoints
  // enforce the true gate server-side. Station-wide readers stay eligible via
  // canManageEvidenceShares regardless.
  const isUploaderOrUnknown =
    videoLoadFailed || (!!video?.uploadedBy && video.uploadedBy === user?.id);
  const canShare = canManageEvidenceShares(permissions, isUploaderOrUnknown);
  /**
   * Performs the actual save. Resolves with the new head version number, or null
   * when the save fails / the server doesn't confirm it.
   */
  const performSave = async (): Promise<number | null> => {
    if (!evidenceId || !draft) return null;
    // Mirror PendingEditsBar's canSave gate: never persist an invalid draft,
    // even when triggered indirectly (e.g. the download flow saves first).
    if (!validation.allValid) {
      toast.error(
        `${invalidCount} ${invalidCount === 1 ? "entry needs" : "entries need"} attention before saving.`,
      );
      return null;
    }
    const payload = stripUids(draft);
    try {
      const res = await mutate(evidenceId, { transcript: payload });
      const version = (res as { versionNumber?: number } | undefined)
        ?.versionNumber;
      if (typeof version !== "number") {
        // Server responded but didn't confirm the save — keep the edits (and
        // the enabled Save button) so the user can retry.
        toast.error("Save didn't complete. Please try again.");
        return null;
      }
      toast.success("Transcript saved");
      // Re-baseline to the saved document + new head version (clears dirty state).
      initStore({ transcript: payload, versionNumber: version }, evidenceId);
      // Follow the new head so the view reflects the saved version and the
      // "viewing old version" banner doesn't reappear once refetchVersions lands.
      setViewingVersion(version);
      return version;
    } catch (err) {
      const updateErr = err as { status?: number; message: string };
      if (updateErr.status === 403) {
        setEditingDisabled(true);
        toast.error("You don't have permission to edit this transcript.");
      } else {
        toast.error(updateErr.message);
      }
      return null;
    }
  };

  // Keep the download hook's save callback pointed at the latest performSave. A
  // save always appends a new head version, so refresh history to follow it.
  saveForDownloadRef.current = async () => {
    const version = await performSave();
    if (version != null) refetchVersions();
    return version;
  };

  const handleConfirmSave = async () => {
    const version = await performSave();
    if (version != null) {
      setSaveDialogOpen(false);
      // A save always appends a new head version; refresh history + follow it.
      refetchVersions();
    }
  };

  /** Loads a specific saved version into the editor as a fresh working copy. */
  const loadVersion = async (versionNumber: number) => {
    if (!evidenceId) return;
    setLoadingVersion(true);
    try {
      const loadedVersion = await getTranscriptVersion(
        evidenceId,
        versionNumber,
      );
      // Load as a fresh working copy. Saving always appends a new head version,
      // so editing an older version naturally becomes the newest one on save.
      initStore(loadedVersion, evidenceId);
      setViewingVersion(loadedVersion.versionNumber);
      setHistoryOpen(false);
    } catch {
      toast.error("Failed to load that version.");
    } finally {
      setLoadingVersion(false);
    }
  };

  const handleSelectVersion = (versionNumber: number) => {
    if (versionNumber === viewingVersion) {
      setHistoryOpen(false);
      return;
    }
    // Guard unsaved edits before swapping the whole working copy.
    if (isDirty) {
      setPendingVersion(versionNumber);
    } else {
      void loadVersion(versionNumber);
    }
  };

  const showLoading = isLoading && !draft;

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
          gap: 1,
        }}
      >
        <Button
          startIcon={<ArrowBack />}
          onClick={requestBack}
          sx={{ textTransform: "none" }}
        >
          Back to transcripts
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            data-tour="history"
            variant="outlined"
            size="small"
            startIcon={<HistoryIcon />}
            onClick={() => setHistoryOpen(true)}
            disabled={!draft}
            sx={{ textTransform: "none" }}
          >
            History
          </Button>
          <Button
            data-tour="manage-speakers"
            variant="outlined"
            size="small"
            startIcon={<GroupIcon />}
            onClick={() => setSpeakerMgrOpen(true)}
            disabled={!draft}
            sx={{ textTransform: "none" }}
          >
            Manage speakers
          </Button>
          {canDownload && (<>
            <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={(e) => download.openDownloadMenu(e.currentTarget)}
            disabled={!draft}
            sx={{ textTransform: "none" }}
          >
            Download
          </Button>
          <Menu
            anchorEl={download.downloadMenuAnchor}
            open={Boolean(download.downloadMenuAnchor)}
            onClose={download.closeDownloadMenu}
            onClick={download.closeDownloadMenu}
          >
            <MenuItem onClick={() => download.selectFormat("txt")}>
              Download as TXT
            </MenuItem>
            <MenuItem onClick={() => download.selectFormat("docx")}>
              Download as DOCX
            </MenuItem>
          </Menu>
          </>)}
          {canShare && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              onClick={() => setShareOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Share
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setTutorialOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Help
          </Button>
        </Box>
      </Box>

      {showLoading ? (
        <Stack spacing={2}>
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={90}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      ) : error && !draft ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      ) : draft ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 2, md: 4 },
            maxWidth: 1280,
            mx: "auto",
          }}
        >
          {/* Video pane — sticky on wide screens */}
          <Box
            data-tour="time-controls"
            sx={{
              width: { xs: "100%", md: 360, lg: 420 },
              flexShrink: 0,
              alignSelf: { xs: "stretch", md: "flex-start" },
              position: { md: "sticky" },
              // Clear the fixed app header so the video doesn't tuck under it.
              top: { md: 88 },
            }}
          >
            {video ? (
              <EditorVideoPlayer
                video={video}
                controller={controller}
                scrubber={
                  <TimeRangeSelector
                    duration={controller.duration}
                    currentTime={controller.currentTime}
                    onScrub={controller.seekTo}
                    markers={markers}
                    speakers={speakers}
                    armed={arming.armed}
                    armedLabel={armedLabel}
                    preview={previewTimeControls}
                    canCommit={arming.canCommit}
                    onSetStart={arming.setStart}
                    onSetEnd={arming.setEnd}
                    onCommit={arming.commit}
                    onCancel={arming.cancel}
                  />
                }
              />
            ) : (
              <Skeleton
                variant="rectangular"
                height={260}
                sx={{ borderRadius: 2 }}
              />
            )}
          </Box>

          {/* Transcript pane */}
          <Box sx={{ flexGrow: 1, minWidth: 0, width: "100%" }}>
            {viewingOldVersion && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() =>
                      latestVersion != null && handleSelectVersion(latestVersion)
                    }
                  >
                    Back to latest
                  </Button>
                }
              >
                Viewing version {viewingVersion} of {latestVersion}. Saving will create a new version from these changes.
              </Alert>
            )}
            <Box data-tour="add-entry">
              <AddEntryComposer
                speakers={speakers}
                isArmingNew={arming.armed?.target.kind === "new"}
                onArmNew={(speaker, text) => arming.armNew(speaker, text)}
                onCancel={arming.cancel}
                disabled={disabled}
              />
            </Box>
            <Box data-tour="entries">
              <TranscriptEntryList
                currentTime={controller.currentTime}
                seekTo={controller.seekTo}
                disabled={disabled}
                validation={validation}
                armedUid={arming.armedUid}
                armingActive={arming.armed !== null}
                onArmEntry={arming.armEntry}
                onArmNewAt={(start, speaker, text) =>
                  arming.armNew(speaker, text, start)
                }
              />
            </Box>
            <PendingEditsBar
              visible={isDirty || canRedo}
              dirty={isDirty}
              isSaving={isSaving}
              saveDisabled={editingDisabled || !canSave}
              invalidCount={invalidCount}
              onDiscard={discardChanges}
              onSave={() => setSaveDialogOpen(true)}
              canUndo={canUndo}
              canRedo={canRedo}
              onUndo={() => undo()}
              onRedo={() => redo()}
            />
          </Box>
        </Box>
      ) : null}

      <DownloadTranscriptLanguageModal
        open={download.languageModalFormat !== null}
        onClose={download.closeLanguageModal}
        onDownload={download.handleLanguageDownload}
      />

      <ShareEvidenceModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        evidenceId={evidenceId}
        videoTitle={video?.title}
      />

      <SpeakerManagerDialog
        open={speakerMgrOpen}
        onClose={() => {
          // While the walkthrough is driving this dialog, ignore manual closes
          // (backdrop click / Escape) so the user can't dismiss it and get
          // stuck — the tour's own buttons advance/close it instead.
          if (tutorialReveal === "speakers-dialog") return;
          setSpeakerMgrOpen(false);
        }}
      />

      <SpeakerQuickAssignDialog
        open={quickAssignOpen}
        onClose={() => setQuickAssignOpen(false)}
        video={video}
      />

      <TranscriptTutorialDialog
        open={tutorialOpen}
        onClose={() => {
          // Releasing the deferral lets the quick-assign effect re-run and open
          // the name prompt now that the walkthrough is done.
          tutorialActive.current = false;
          setTutorialOpen(false);
        }}
        onRevealChange={setTutorialReveal}
      />

      <SaveDiffDialog
        open={saveDialogOpen}
        diff={diff}
        isSaving={isSaving}
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveDialogOpen(false)}
      />

      <TranscriptHistoryDialog
        open={historyOpen}
        versions={versions}
        isLoading={versionsLoading || loadingVersion}
        currentVersion={viewingVersion}
        latestVersion={latestVersion}
        isDirty={isDirty}
        resolveName={resolveName}
        onSelect={handleSelectVersion}
        onClose={() => {
          // See SpeakerManagerDialog: block manual closes while the tour drives
          // this dialog so the walkthrough stays in a consistent state.
          if (tutorialReveal === "history-dialog") return;
          setHistoryOpen(false);
        }}
      />

      <ConfirmDialog
        open={pendingVersion !== null}
        onClose={() => setPendingVersion(null)}
        onConfirm={() => {
          if (pendingVersion !== null) void loadVersion(pendingVersion);
          setPendingVersion(null);
        }}
        tone="warning"
        title="Discard unsaved changes?"
        description={
          pendingVersion !== null
            ? `Opening version ${pendingVersion} will discard your current unsaved changes.`
            : undefined
        }
        confirmLabel="Discard and open"
      />

      <ConfirmDialog
        open={pendingBack !== false}
        onClose={() => setPendingBack(false)}
        onConfirm={() => {
          const source = pendingBack;
          setPendingBack(false);
          if (source === "browser") {
            // Real browser-back: hop over the sentinel plus the guarded entry
            // in one pop. The guard's popstate handler sees the pathname
            // change and stands down; unmount then removes it entirely.
            navigate(-2);
          } else {
            // In-page back buttons. Replace (not push) so the sentinel entry
            // the dirty guard pushed doesn't linger behind the list page as a
            // dead back-press.
            navigate("/transcripts", { replace: true });
          }
        }}
        tone="warning"
        title="Discard unsaved changes?"
        description="Leaving this transcript will discard your current unsaved changes."
        confirmLabel="Discard and leave"
      />
    </Container>
  );
};

export default TranscriptViewPage;
